import React from 'react';
import styled from 'styled-components';
import { Body5, Container } from '@ui';
import { SkeletonButton, TransparentFlatButton } from '@ui/buttons';
import ErrorText from '@ui/ErrorText';
import { DOCUMENT_DOWNLOAD_KINDS } from '@utils/documentVault';
import { DownloadQueueContext, QUEUE_ITEM_STATUS } from '@/contexts/DownloadQueueContext';

const PanelWrapper = styled.div`
  position: fixed;
  right: 24px;
  bottom: 24px;
  width: 360px;
  max-height: 45vh;
  overflow-y: auto;
  z-index: 10002;

  background: ${({ theme }) => theme.themeColors.appBackground};
  border: 1px solid ${({ theme }) => theme.themeColors.border};
  border-radius: 6px;
  box-shadow: 0 2px 8px 1px rgba(0, 0, 0, 0.14);
  padding: 16px;
`;

const HeaderWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const ItemRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;

  padding: 6px 0;
  border-bottom: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
`;

const StatusGlyph = styled.span`
  width: 14px;
  flex-shrink: 0;
  text-align: center;

  color: ${({ theme, $status }) =>
    $status === QUEUE_ITEM_STATUS.ERROR
      ? theme.themeColors.error
      : $status === QUEUE_ITEM_STATUS.COMPLETED
      ? theme.themeColors.primaryCtaButton
      : theme.themeColors.secondaryText};
`;

const FilenameWrapper = styled.div`
  flex: 1;
  min-width: 0;
`;

const STATUS_GLYPHS = {
  [QUEUE_ITEM_STATUS.QUEUED]: '·',
  [QUEUE_ITEM_STATUS.RUNNING]: '⟳',
  [QUEUE_ITEM_STATUS.COMPLETED]: '✓',
  [QUEUE_ITEM_STATUS.ERROR]: '✕',
  [QUEUE_ITEM_STATUS.CANCELED]: '–',
};

const RunningElapsed = () => {
  const [seconds, setSeconds] = React.useState(0);

  React.useEffect(() => {
    const startedAt = Date.now();
    const interval = setInterval(() => setSeconds(Math.floor((Date.now() - startedAt) / 1000)), 1000);
    return () => clearInterval(interval);
  }, []);

  const minutes = Math.floor(seconds / 60);
  return (
    <Body5 isLowContrast>
      {minutes}:{String(seconds % 60).padStart(2, '0')}
    </Body5>
  );
};

export const DownloadQueuePanel = ({ docsById }) => {
  const { items, isProcessing, kind, summary, cancel, dismiss, retryFailed } = React.useContext(DownloadQueueContext);

  if (items.length === 0) return null;

  const isCleanCopyRun = kind === DOCUMENT_DOWNLOAD_KINDS.clean;

  const completedCount = items.filter((item) => item.status === QUEUE_ITEM_STATUS.COMPLETED).length;
  const failedCount = summary?.failed?.length || 0;

  return (
    <PanelWrapper role="status" aria-live="polite">
      <HeaderWrapper>
        <Body5 bold>
          {isProcessing
            ? `${isCleanCopyRun ? 'Preparing clean copies' : 'Preparing downloads'} — ${completedCount} of ${
                items.length
              }`
            : // Says plainly how many of how many made it, rather than implying a clean sweep.
              `Downloaded ${summary?.succeeded ?? completedCount} of ${summary?.total ?? items.length}`}
        </Body5>
        {isProcessing ? (
          <SkeletonButton onClick={cancel}>Cancel</SkeletonButton>
        ) : (
          <SkeletonButton onClick={dismiss}>Dismiss</SkeletonButton>
        )}
      </HeaderWrapper>

      {isProcessing && isCleanCopyRun ? (
        <Container top={4}>
          {/* A clean copy re-runs the whole pipeline server side, so minutes are normal here. */}
          <Body5 isLowContrast>Each clean copy can take up to 3 minutes.</Body5>
        </Container>
      ) : null}

      <Container top={12}>
        {items.map((item) => (
          <ItemRow key={item.id}>
            <StatusGlyph $status={item.status}>{STATUS_GLYPHS[item.status]}</StatusGlyph>
            <FilenameWrapper>
              <Body5 truncate>{item.filename}</Body5>
            </FilenameWrapper>
            {item.status === QUEUE_ITEM_STATUS.RUNNING && isCleanCopyRun ? <RunningElapsed /> : null}
            {item.status === QUEUE_ITEM_STATUS.ERROR ? <ErrorText>Failed</ErrorText> : null}
          </ItemRow>
        ))}
      </Container>

      {!isProcessing && failedCount > 0 && !isCleanCopyRun ? (
        <Container top={12}>
          <ErrorText>
            {failedCount} {failedCount === 1 ? 'file' : 'files'} couldn’t be downloaded.
          </ErrorText>
          <Container top={8}>
            {/* Retries only the failures, not the whole selection. */}
            <TransparentFlatButton fullWidth onClick={() => retryFailed(docsById)}>
              Retry failed
            </TransparentFlatButton>
          </Container>
        </Container>
      ) : null}
    </PanelWrapper>
  );
};
